import { useState, useEffect, useMemo, useRef, useId } from "react";
import { useParams, Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { sendJudgeSignInLink, useAuth } from "../hooks/useAuth";
import {
  subscribeToEvent, subscribeToDisciplines, subscribeToEventBouts,
  addParticipants, removeParticipant, createBout, updateBout, createDiscipline,
  setEventPublicVisibility, setSweepsVisibility, updateEventDetails, updateEventWelcomeContent,
  updateEventDisciplineSchedules,
  addEventOfficials, removeEventOfficial, updateEventOfficialAffiliations, updateEventOfficialRoles,
  updateEventOfficialEmails, migratePrivateEventData,
  addEventFighters, removeEventFighter, updateEventFighterAffiliations, updateEventFighterEmails,
  updateEventFighterMismatchDismissed, approveFighterClaim, clearFighterFromPendingClaims,
  updateEventFighterSharedEmailDismissed,
  setFighterDisciplines, updateEventFighterDisciplines, updateEventFighterWaivers,
  importRosterData,
  updateEventFighterWelcomeEmail, updateEventFighterCustomEmail, uploadRichContentImage
} from "../firebase/services";
import { PHASES, ROUND_OPTIONS } from "../utils/scoring";
import { DISCIPLINE_PRESETS } from "../utils/disciplinePresets";
import { calculateSweepstakes } from "../utils/sweepstakes";
import { formatDateRange } from "../utils/formatDateRange";
import { addHoursToTime } from "../utils/scheduleTime";
import { parseRosterFile, classifyHeaders, buildRosterSummary, resolveRosterDisciplines, extractRosterNames } from "../utils/rosterImport";
import RosterColumnMapping from "../components/RosterColumnMapping";
import RosterImportPreview from "../components/RosterImportPreview";
import SweepstakesResults, { SweepstakesInfo } from "../components/Sweepstakes";
import RichTextEditor from "../components/RichTextEditor";
import InlineTextCell from "../components/InlineTextCell";
import TimeEntryCell from "../components/TimeEntryCell";
import { sanitizeHtml, isHtmlEmpty, toEditableHtml, escapeHtml } from "../utils/sanitizeHtml";
import CodeQR from "../components/CodeQR";
import Toast from "../components/Toast";
import { useToast } from "../hooks/useToast";
import logo from "../assets/logo.png";

export default function EventDashboard() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [bouts, setBouts] = useState([]);
  const autoPoolStartTimesRef = useRef(new Map());

  // Tabs (tournament mode only)
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "settings";
  const setActiveTab = (tab) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set("tab", tab);
    return next;
  }, { replace: true });

  // Seminar/Training: roster + bout creation
  const [newNames, setNewNames] = useState("");
  const [adding, setAdding] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [fighterBlue, setFighterBlue] = useState("");
  const [fighterRed, setFighterRed] = useState("");
  const [selectedRef, setSelectedRef] = useState("");
  const [selectedJudges, setSelectedJudges] = useState([]);
  const [phase, setPhase] = useState("pool");
  const [totalRounds, setTotalRounds] = useState(1);
  const [boutTime, setBoutTime] = useState("");
  const [boutLocation, setBoutLocation] = useState("");
  const [creatingBout, setCreatingBout] = useState(false);
  // Names read from an uploaded participant file, awaiting confirmation
  const [participantImport, setParticipantImport] = useState(null);
  const [participantParsing, setParticipantParsing] = useState(false);
  // Bumped after creating a bout so the bout list re-subscribes: the new bout's
  // snapshot can arrive before the owner code cache is invalidated, which would
  // otherwise leave the card without its codes until the bout next changes.
  const [boutsRefresh, setBoutsRefresh] = useState(0);

  // Tournament: add discipline
  const [showAddDiscipline, setShowAddDiscipline] = useState(false);
  const [showDisciplineDropdown, setShowDisciplineDropdown] = useState(false);
  const [customDisciplineName, setCustomDisciplineName] = useState("");
  const [addingDiscipline, setAddingDiscipline] = useState(false);

  // Tournament: event-wide judges & refs roster
  const [newOfficials, setNewOfficials] = useState("");
  const [addingOfficials, setAddingOfficials] = useState(false);
  const officialsMigrated = useRef(false);
  // email -> true/false once checked against Firebase Auth; absent while unchecked/checking
  const [registrationStatus, setRegistrationStatus] = useState({});
  const [invitingEmail, setInvitingEmail] = useState(null);

  // Tournament: event-wide fighter roster
  const [newFighters, setNewFighters] = useState("");
  const [addingFighters, setAddingFighters] = useState(false);
  const fightersMigrated = useRef(false);
  const [rosterParsed, setRosterParsed] = useState(null);
  const [rosterMapping, setRosterMapping] = useState(null);
  const [rosterSummary, setRosterSummary] = useState(null);
  const [rosterParsing, setRosterParsing] = useState(false);
  const [rosterImporting, setRosterImporting] = useState(false);
  // email -> { registered: true/false/null, displayName } once checked against Firebase Auth
  const [fighterAccountInfo, setFighterAccountInfo] = useState({});
  const [claimActionUid, setClaimActionUid] = useState(null);
  const privateDataMigrationAttempted = useRef(false);

  // Settings
  const [togglingVisibility, setTogglingVisibility] = useState(false);
  const [togglingSweeps, setTogglingSweeps] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [savingWelcome, setSavingWelcome] = useState(false);
  const [savingFighterWelcomeEmail, setSavingFighterWelcomeEmail] = useState(false);
  const [savingFighterCustomEmail, setSavingFighterCustomEmail] = useState(false);
  const { toast, showToast } = useToast();

  useEffect(() => {
    const unsub1 = subscribeToEvent(eventId, setEvent);
    const unsub2 = subscribeToDisciplines(eventId, setDisciplines);
    return () => { unsub1(); unsub2(); };
  }, [eventId]);

  useEffect(() => subscribeToEventBouts(eventId, setBouts), [eventId, boutsRefresh]);

  // Once the owner-only Firestore rule is live, migrate any legacy sensitive
  // fields through the authenticated server endpoint. The service first proves
  // this client can read the destination, so an old rules deployment can never
  // move data somewhere the dashboard cannot load it.
  useEffect(() => {
    if (privateDataMigrationAttempted.current || !event || event.createdBy !== user?.uid) return;
    privateDataMigrationAttempted.current = true;
    migratePrivateEventData(eventId).catch((error) => {
      console.error("Private event-data migration deferred:", error);
    });
  }, [event, eventId, user]);

  const sweepstakes = useMemo(
    () => calculateSweepstakes(disciplines, bouts, event?.fighterAffiliations || {}),
    [disciplines, bouts, event?.fighterAffiliations]
  );

  // One-time backfill for events created before judges/refs moved to an event-wide
  // roster: folds any names/affiliations already sitting on individual disciplines
  // (from the old per-discipline roster) up onto the event doc, so nothing already
  // entered by an admin appears to vanish once this tab exists.
  useEffect(() => {
    if (officialsMigrated.current || !event || disciplines.length === 0) return;
    const eventOfficials = event.officials || [];
    const eventAffiliations = event.officialAffiliations || {};
    const missingNames = new Set();
    const missingAffiliations = {};
    for (const disc of disciplines) {
      for (const name of disc.officials || []) {
        if (!eventOfficials.includes(name)) missingNames.add(name);
      }
      for (const [name, affiliation] of Object.entries(disc.officialAffiliations || {})) {
        if (!eventAffiliations[name] && affiliation) missingAffiliations[name] = affiliation;
      }
    }
    if (missingNames.size === 0 && Object.keys(missingAffiliations).length === 0) return;
    officialsMigrated.current = true;
    (async () => {
      if (missingNames.size > 0) await addEventOfficials(eventId, [...missingNames]);
      if (Object.keys(missingAffiliations).length > 0) {
        await updateEventOfficialAffiliations(eventId, { ...eventAffiliations, ...missingAffiliations });
      }
    })().catch((err) => console.error(err));
  }, [event, disciplines, eventId]);

  // Same backfill as above, for fighters: folds each discipline's disc.fighters
  // and disc.fighterAffiliations up onto the event doc's new central roster, so
  // events created before this feature existed don't appear to lose fighters.
  // No email is backfilled this way (disciplines never captured one) — same
  // limitation officials already have post-migration, not a new gap.
  useEffect(() => {
    if (fightersMigrated.current || !event || disciplines.length === 0) return;
    const eventFighters = event.fighters || [];
    const eventAffiliations = event.fighterAffiliations || {};
    const missingNames = new Set();
    const missingAffiliations = {};
    for (const disc of disciplines) {
      for (const name of disc.fighters || []) {
        if (!eventFighters.includes(name)) missingNames.add(name);
      }
      for (const [name, affiliation] of Object.entries(disc.fighterAffiliations || {})) {
        if (!eventAffiliations[name] && affiliation) missingAffiliations[name] = affiliation;
      }
    }
    if (missingNames.size === 0 && Object.keys(missingAffiliations).length === 0) return;
    fightersMigrated.current = true;
    (async () => {
      if (missingNames.size > 0) await addEventFighters(eventId, [...missingNames]);
      if (Object.keys(missingAffiliations).length > 0) {
        await updateEventFighterAffiliations(eventId, { ...eventAffiliations, ...missingAffiliations });
      }
    })().catch((err) => console.error(err));
  }, [event, disciplines, eventId]);

  // Backfills event.fighterDisciplines (this new feature's registration map) from
  // each discipline's existing disc.fighters[] membership, so fighters added back
  // when discipline pages still had their own add/remove UI don't appear
  // unregistered from every discipline the first time this loads. Only fills in
  // discipline ids missing from a fighter's entry — never removes one — so it can't
  // clobber an admin's own edits made after this ran once.
  const fighterDisciplinesMigrated = useRef(false);
  useEffect(() => {
    if (fighterDisciplinesMigrated.current || !event || disciplines.length === 0) return;
    const current = event.fighterDisciplines || {};
    const next = {};
    for (const disc of disciplines) {
      for (const name of disc.fighters || []) {
        const existing = next[name] || current[name] || [];
        if (!existing.includes(disc.id)) next[name] = [...existing, disc.id];
        else if (!next[name]) next[name] = existing;
      }
    }
    const changed = Object.keys(next).some(name => {
      const before = current[name] || [];
      const after = next[name];
      return after.length !== before.length || after.some(id => !before.includes(id));
    });
    if (!changed) return;
    fighterDisciplinesMigrated.current = true;
    updateEventFighterDisciplines(eventId, { ...current, ...next }).catch((err) => console.error(err));
  }, [event, disciplines, eventId]);

  // Backfills event.fighterWaivers from each discipline's old disc.fighterWaivers
  // (waivers used to be tracked per discipline; now one signature covers the whole
  // tournament) — a fighter counts as signed here if any discipline already had them
  // marked signed. Only ever turns entries on, never off, so it can't re-litigate a
  // signature an admin already unmarked at the event level.
  const fighterWaiversMigrated = useRef(false);
  useEffect(() => {
    if (fighterWaiversMigrated.current || !event || disciplines.length === 0) return;
    const current = event.fighterWaivers || {};
    const missing = {};
    for (const disc of disciplines) {
      for (const [name, signed] of Object.entries(disc.fighterWaivers || {})) {
        if (signed && !current[name]) missing[name] = true;
      }
    }
    if (Object.keys(missing).length === 0) return;
    fighterWaiversMigrated.current = true;
    updateEventFighterWaivers(eventId, { ...current, ...missing }).catch((err) => console.error(err));
  }, [event, disciplines, eventId]);

  // Registration status is checked live against Firebase Auth (not stored in
  // Firestore) so it can't go stale — a judge registering from their own device
  // shows up here next time this tab loads without the admin doing anything.
  // The check itself runs server-side (api/checkRegistration.js, Admin SDK's
  // getUserByEmail) rather than via the client SDK's fetchSignInMethodsForEmail,
  // since that client call always returns [] when this Firebase project has
  // Email Enumeration Protection enabled — the Admin SDK isn't subject to that
  // restriction.
  useEffect(() => {
    const emails = event?.officialEmails || {};
    const toCheck = [...new Set(Object.values(emails).filter(Boolean))]
      .filter(email => !(email in registrationStatus));
    if (toCheck.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { status } = await fetchRegistrationInfo(user, eventId, toCheck);
        if (!cancelled) setRegistrationStatus(prev => ({ ...prev, ...status }));
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setRegistrationStatus(prev => ({
            ...prev, ...Object.fromEntries(toCheck.map(email => [email, null])),
          }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [event?.officialEmails, registrationStatus, eventId, user]);

  // Same live-registration check as above, but for the fighter roster, and also keeping
  // the registered account's display name (api/checkRegistration.js returns both) — the
  // FIGHTERS tab uses that to flag "this email is registered under a different name than
  // this roster entry" typos, e.g. an admin accidentally entering their own email for a
  // fighter (see FightersPanel).
  useEffect(() => {
    const emails = event?.fighterEmails || {};
    const toCheck = [...new Set(Object.values(emails).map(e => (e || "").trim()).filter(Boolean))]
      .filter(email => !(email in fighterAccountInfo));
    if (toCheck.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { status, names } = await fetchRegistrationInfo(user, eventId, toCheck);
        if (!cancelled) {
          setFighterAccountInfo(prev => {
            const next = { ...prev };
            for (const email of toCheck) next[email] = { registered: status[email], displayName: (names || {})[email] || null };
            return next;
          });
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setFighterAccountInfo(prev => ({
            ...prev, ...Object.fromEntries(toCheck.map(email => [email, { registered: null, displayName: null }])),
          }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [event?.fighterEmails, fighterAccountInfo, eventId, user]);

  const handleAddOfficials = async (e) => {
    e.preventDefault();
    if (!newOfficials.trim()) return;
    setAddingOfficials(true);
    try {
      await addEventOfficials(eventId, newOfficials.split("\n").map(n => n.trim()).filter(Boolean));
      setNewOfficials("");
    } catch (err) { console.error(err); alert("Failed to add"); }
    setAddingOfficials(false);
  };

  const handleRemoveOfficial = async (name) => {
    if (!window.confirm(`Remove ${name} from the event's judges & refs roster?`)) return;
    try {
      await removeEventOfficial(eventId, name, event.officials || []);
    } catch (err) { console.error(err); alert("Failed to remove"); }
  };

  const handleUpdateOfficialAffiliation = async (name, affiliation) => {
    const updated = { ...(event.officialAffiliations || {}), [name]: affiliation };
    try { await updateEventOfficialAffiliations(eventId, updated); }
    catch (err) { console.error(err); }
  };

  const handleUpdateOfficialRole = async (name, role) => {
    const updated = { ...(event.officialRoles || {}), [name]: role };
    try { await updateEventOfficialRoles(eventId, updated); }
    catch (err) { console.error(err); }
  };

  const handleUpdateOfficialEmail = async (name, email) => {
    const updated = { ...(event.officialEmails || {}), [name]: email.trim() };
    try { await updateEventOfficialEmails(eventId, updated); }
    catch (err) { console.error(err); }
  };

  const handleInviteOfficial = async (email) => {
    setInvitingEmail(email);
    try {
      await sendJudgeSignInLink(email);
      showToast(`Invite sent to ${email}`);
    } catch (err) { console.error(err); alert("Failed to send invite"); }
    setInvitingEmail(null);
  };

  const handleAddFighters = async (e) => {
    e.preventDefault();
    if (!newFighters.trim()) return;
    setAddingFighters(true);
    try {
      await addEventFighters(eventId, newFighters.split("\n").map(n => n.trim()).filter(Boolean));
      setNewFighters("");
    } catch (err) { console.error(err); alert("Failed to add"); }
    setAddingFighters(false);
  };

  const handleRemoveFighter = async (name) => {
    if (!window.confirm(`Remove ${name} from the event's fighter roster? This also removes them from every discipline they're registered for.`)) return;
    try {
      await removeEventFighter(eventId, name, event.fighters || [], disciplines, event.fighterDisciplines || {});
    } catch (err) { console.error(err); alert("Failed to remove"); }
  };

  const handleRosterFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    setRosterParsing(true);
    try {
      const parsed = await parseRosterFile(file);
      setRosterParsed(parsed);
      setRosterMapping(classifyHeaders(parsed.headers));
    } catch (err) {
      console.error(err);
      alert("Failed to read roster file");
    }
    setRosterParsing(false);
  };

  const handleConfirmRosterMapping = (mapping) => {
    setRosterSummary(buildRosterSummary(rosterParsed, mapping));
    setRosterParsed(null);
    setRosterMapping(null);
  };

  const handleCancelRosterImport = () => {
    setRosterParsed(null);
    setRosterMapping(null);
    setRosterSummary(null);
  };

  const handleConfirmRosterImport = async (selections, includeJudges) => {
    const rosters = resolveRosterDisciplines(rosterSummary, selections);
    setRosterImporting(true);
    try {
      await importRosterData(eventId, {
        rosters,
        fighterEmails: rosterSummary.fighterEmails || {},
        fighterAffiliations: rosterSummary.fighterAffiliations || {},
        judgeNames: includeJudges ? rosterSummary.judgeNames || [] : [],
        existingDisciplines: disciplines,
        existingFighterDisciplines: event.fighterDisciplines || {},
        existingFighterEmails: event.fighterEmails || {},
        existingFighterAffiliations: event.fighterAffiliations || {},
        existingOfficialAffiliations: event.officialAffiliations || {},
        existingOfficialRoles: event.officialRoles || {},
      });
      setRosterSummary(null);
      const disciplineCount = Object.keys(rosters).length;
      const fighterCount = new Set(Object.values(rosters).flat()).size;
      const judgeCount = includeJudges ? (rosterSummary.judgeNames || []).length : 0;
      const parts = [];
      if (fighterCount) parts.push(`${fighterCount} fighter${fighterCount === 1 ? "" : "s"} into ${disciplineCount} discipline${disciplineCount === 1 ? "" : "s"}`);
      if (judgeCount) parts.push(`${judgeCount} judge${judgeCount === 1 ? "" : "s"}`);
      showToast(`Imported ${parts.join(" and ")}`);
    } catch (err) { console.error(err); alert("Failed to import roster"); }
    setRosterImporting(false);
  };

  const handleUpdateFighterAffiliation = async (name, affiliation) => {
    const updated = { ...(event.fighterAffiliations || {}), [name]: affiliation };
    try { await updateEventFighterAffiliations(eventId, updated); }
    catch (err) { console.error(err); }
  };

  const handleUpdateFighterDisciplines = async (name, disciplineIds) => {
    try {
      await setFighterDisciplines(eventId, name, disciplineIds, disciplines, event.fighterDisciplines || {});
    } catch (err) { console.error(err); alert("Failed to update disciplines"); }
  };

  const handleUpdateFighterEmail = async (name, email) => {
    const updated = { ...(event.fighterEmails || {}), [name]: email.trim() };
    try {
      await updateEventFighterEmails(eventId, updated);
      await clearFighterFromPendingClaims(eventId, name, event.pendingFighterClaims || {});
    } catch (err) { console.error(err); }
  };

  const handleDismissFighterMismatch = async (name, displayName) => {
    const updated = { ...(event.fighterMismatchDismissed || {}), [name]: displayName };
    try { await updateEventFighterMismatchDismissed(eventId, updated); }
    catch (err) { console.error(err); }
  };

  const handleApproveFighterClaim = async (uid, names, email) => {
    setClaimActionUid(uid);
    try {
      await approveFighterClaim(eventId, uid, names, email, event.fighterUids || {}, event.pendingFighterClaims || {}, event.fighterSharedEmailDismissed || {});
      showToast("Linked");
    } catch (err) { console.error(err); alert("Failed to approve claim"); }
    setClaimActionUid(null);
  };

  const handleDismissSharedEmail = async (email) => {
    const updated = { ...(event.fighterSharedEmailDismissed || {}), [email.toLowerCase()]: true };
    try { await updateEventFighterSharedEmailDismissed(eventId, updated); }
    catch (err) { console.error(err); }
  };

  const handleSaveFighterWelcomeEmail = async (draft) => {
    setSavingFighterWelcomeEmail(true);
    try {
      await updateEventFighterWelcomeEmail(eventId, draft);
      showToast("Changes saved");
    } catch (err) { console.error(err); alert("Failed to save welcome email"); }
    setSavingFighterWelcomeEmail(false);
  };

  const handleSaveFighterCustomEmail = async (draft) => {
    setSavingFighterCustomEmail(true);
    try {
      await updateEventFighterCustomEmail(eventId, draft);
      showToast("Changes saved");
    } catch (err) { console.error(err); alert("Failed to save update email"); }
    setSavingFighterCustomEmail(false);
  };

  const handleAddParticipants = async (e) => {
    e.preventDefault();
    if (!newNames.trim()) return;
    setAdding(true);
    try {
      await addParticipants(eventId, newNames.split("\n").map(n => n.trim()).filter(Boolean));
      setNewNames("");
    } catch (err) { console.error(err); alert("Failed to add"); }
    setAdding(false);
  };

  const handleParticipantFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    setParticipantParsing(true);
    try {
      const names = extractRosterNames(await parseRosterFile(file));
      if (!names.length) alert("No names found. Use a Name column, or First Name and Last Name columns.");
      else setParticipantImport(names);
    } catch (err) { console.error(err); alert("Failed to read file"); }
    setParticipantParsing(false);
  };

  const handleConfirmParticipantImport = async (names) => {
    setAdding(true);
    try {
      await addParticipants(eventId, names);
      setParticipantImport(null);
      showToast(`Added ${names.length} participant${names.length === 1 ? "" : "s"}`);
    } catch (err) { console.error(err); alert("Failed to add"); }
    setAdding(false);
  };

  const handleRemove = async (name) => {
    if (!window.confirm(`Remove ${name}?`)) return;
    await removeParticipant(eventId, name, event.participants);
  };

  const handleCreateBout = async () => {
    if (!fighterBlue || !fighterRed || !selectedRef) return;
    setCreatingBout(true);
    try {
      await createBout(eventId, {
        fighterBlue, fighterRed, refName: selectedRef, judges: selectedJudges,
        phase, totalRounds, time: boutTime, location: boutLocation,
      });
      setBoutsRefresh((n) => n + 1);
      setFighterBlue(""); setFighterRed(""); setSelectedRef(""); setSelectedJudges([]);
      setBoutTime(""); setBoutLocation(""); setShowCreate(false);
    } catch (err) { console.error(err); alert("Failed to create bout"); }
    setCreatingBout(false);
  };

  const handleTogglePublicVisibility = async () => {
    setTogglingVisibility(true);
    try {
      await setEventPublicVisibility(eventId, !event.isPublic);
    } catch (err) { console.error(err); alert("Failed to update visibility"); }
    setTogglingVisibility(false);
  };

  const handleToggleSweepsVisibility = async () => {
    setTogglingSweeps(true);
    try {
      await setSweepsVisibility(eventId, !event.sweepsVisible);
    } catch (err) { console.error(err); alert("Failed to update sweeps visibility"); }
    setTogglingSweeps(false);
  };

  const handleSaveEventDetails = async (details) => {
    setSavingDetails(true);
    try {
      await updateEventDetails(eventId, details);
      showToast("Changes saved");
    } catch (err) { console.error(err); alert("Failed to save event details"); }
    setSavingDetails(false);
  };

  const handleSaveWelcomeContent = async (content) => {
    setSavingWelcome(true);
    try {
      await updateEventWelcomeContent(eventId, content);
      showToast("Changes saved");
    } catch (err) { console.error(err); alert("Failed to save home page"); }
    setSavingWelcome(false);
  };

  const handleUpdateDisciplineSchedule = async (disciplineId, field, value) => {
    const currentSchedule = event.disciplineSchedules?.[disciplineId] || {};
    const nextSchedule = { ...currentSchedule, [field]: value };
    const previousTrackedPoolStart = autoPoolStartTimesRef.current.get(disciplineId);

    if (field === "poolStartTime") {
      autoPoolStartTimesRef.current.delete(disciplineId);
    } else if (field === "checkInTime") {
      const poolStartIsBlank = !currentSchedule.poolStartTime;
      const poolStartIsStillAutomatic = previousTrackedPoolStart === currentSchedule.poolStartTime;
      if (poolStartIsBlank || poolStartIsStillAutomatic) {
        const suggestedPoolStart = addHoursToTime(value, 1);
        if (suggestedPoolStart) {
          nextSchedule.poolStartTime = suggestedPoolStart;
          autoPoolStartTimesRef.current.set(disciplineId, suggestedPoolStart);
        }
      } else {
        autoPoolStartTimesRef.current.delete(disciplineId);
      }
    }

    const updated = {
      ...(event.disciplineSchedules || {}),
      [disciplineId]: nextSchedule,
    };
    try { await updateEventDisciplineSchedules(eventId, updated); }
    catch (err) { console.error(err); }
  };

  const handleAddDiscipline = async (name) => {
    const trimmed = name.trim();
    if (!trimmed || disciplines.some(d => d.name === trimmed)) return;
    setAddingDiscipline(true);
    try {
      await createDiscipline(eventId, trimmed);
      setCustomDisciplineName("");
      setShowDisciplineDropdown(false);
      setShowAddDiscipline(false);
    } catch (err) { console.error(err); alert("Failed to add discipline"); }
    setAddingDiscipline(false);
  };

  if (!event) return <div className="flex items-center justify-center min-h-screen text-gray-600">Loading...</div>;

  if (event.createdBy && event.createdBy !== user?.uid) {
    return <Navigate to={`/public/${eventId}`} replace />;
  }

  const isTournament = event.type === "tournament";
  const participants = event.participants || [];
  const availableBlue = participants.filter(p => p !== fighterRed);
  const availableRed = participants.filter(p => p !== fighterBlue);
  const availableRefs = participants.filter(p => p !== fighterBlue && p !== fighterRed && !selectedJudges.includes(p));
  const availableJudges = participants.filter(p => p !== fighterBlue && p !== fighterRed && p !== selectedRef);

  // Choosing someone as a fighter takes them out of the ref/judge selections.
  const pickFighter = (setFighter, name) => {
    setFighter(name);
    if (selectedRef === name) setSelectedRef("");
    setSelectedJudges(selectedJudges.filter(j => j !== name));
  };

  const activeBouts = bouts.filter(b => b.status !== "confirmed");
  const completedBouts = bouts.filter(b => b.status === "confirmed");
  const availableDisciplinePresets = DISCIPLINE_PRESETS.filter(p => !disciplines.some(d => d.name === p));

  return (
    <div className="min-h-screen p-5 lg:max-w-5xl lg:mx-auto">
      <Toast toast={toast} />
      <Link to="/account" className="text-gray-600 text-sm no-underline hover:text-gray-400">← My Account</Link>

      <div className="text-center my-4">
        <img src={logo} alt="Joy of HEMA" className="w-80 mx-auto" />
        <div className="flex items-center gap-3 max-w-[280px] mx-auto mt-2">
          <div className="flex-1 h-px bg-dark-500" />
          <span className="text-gray-400 text-sm font-display font-bold tracking-[0.25em] whitespace-nowrap">
            STUDY &bull; FENCE &bull; GROW
          </span>
          <div className="flex-1 h-px bg-dark-500" />
        </div>
      </div>

      <h1 className="font-display text-xl font-bold mt-3 mb-1">{event.name}</h1>
      <div className="text-gray-600 text-xs mb-4">
        {event.type === "tournament" ? "Tournament" : event.type === "seminar" ? "Judging Seminar" : "Training"}
        {event.startDate && <span> • {formatDateRange(event.startDate, event.endDate)}</span>}
        {event.location && <span> • {event.location}</span>}
      </div>

      {/* Event Code */}
      <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display text-xs font-bold text-gray-500 tracking-widest">EVENT CODE</div>
            <div className="font-display text-2xl font-extrabold tracking-[0.2em] text-white mt-1">{event.eventCode}</div>
          </div>
          <div className="text-right text-gray-600 text-xs">
            Share with refs<br />& judges
          </div>
        </div>
      </div>

      {/* TOURNAMENT MODE: Disciplines / Sweeps tabs */}
      {isTournament && (
        <div>
          {/* Tab bar */}
          <div className="flex gap-1 mb-5 border-b border-dark-500 overflow-x-auto">
            {[
              { key: "settings", label: "SETTINGS" },
              { key: "fighters", label: `FIGHTERS${(event.fighters?.length) ? ` (${event.fighters.length})` : ""}` },
              { key: "judges", label: `JUDGES${(event.officials?.length) ? ` (${event.officials.length})` : ""}` },
              { key: "disciplines", label: "DISCIPLINES" },
              { key: "communications", label: "COMMUNICATIONS" },
              { key: "sweeps-info", label: "SWEEPS INFO" },
              { key: "sweeps-results", label: "SWEEPS RESULTS" },
            ].map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)} aria-pressed={activeTab === t.key}
                className="px-4 py-2.5 font-display text-xs font-bold tracking-widest whitespace-nowrap border-b-2 bg-transparent cursor-pointer"
                style={{ color: activeTab === t.key ? "#fff" : "#bbb", borderColor: activeTab === t.key ? "#81C784" : "transparent" }}>
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === "judges" && (
            <JudgesPanel event={event} newOfficials={newOfficials} setNewOfficials={setNewOfficials}
              addingOfficials={addingOfficials} onAdd={handleAddOfficials} onRemove={handleRemoveOfficial}
              onUpdateAffiliation={handleUpdateOfficialAffiliation} onUpdateRole={handleUpdateOfficialRole}
              onUpdateEmail={handleUpdateOfficialEmail} onInvite={handleInviteOfficial}
              registrationStatus={registrationStatus} invitingEmail={invitingEmail} />
          )}

          {activeTab === "fighters" && (
            <FightersPanel event={event} disciplines={disciplines} newFighters={newFighters} setNewFighters={setNewFighters}
              addingFighters={addingFighters} onAdd={handleAddFighters} onRemove={handleRemoveFighter}
              onUpdateAffiliation={handleUpdateFighterAffiliation} onUpdateEmail={handleUpdateFighterEmail}
              onUpdateDisciplines={handleUpdateFighterDisciplines}
              fighterAccountInfo={fighterAccountInfo} onDismissMismatch={handleDismissFighterMismatch}
              onApproveClaim={handleApproveFighterClaim} claimActionUid={claimActionUid}
              onDismissSharedEmail={handleDismissSharedEmail}
              rosterParsed={rosterParsed} rosterMapping={rosterMapping} rosterSummary={rosterSummary}
              rosterParsing={rosterParsing} rosterImporting={rosterImporting}
              onRosterFile={handleRosterFile} onConfirmRosterMapping={handleConfirmRosterMapping}
              onConfirmRosterImport={handleConfirmRosterImport} onCancelRosterImport={handleCancelRosterImport} />
          )}

          {activeTab === "communications" && (
            <CommunicationsPanel event={event} disciplines={disciplines} user={user}
              onSaveWelcomeEmail={handleSaveFighterWelcomeEmail} savingWelcomeEmail={savingFighterWelcomeEmail}
              onSaveCustomEmail={handleSaveFighterCustomEmail} savingCustomEmail={savingFighterCustomEmail}
              showToast={showToast} />
          )}

          {activeTab === "sweeps-info" && <SweepstakesInfo />}

          {activeTab === "sweeps-results" && <SweepstakesResults schools={sweepstakes.schools} />}

          {activeTab === "settings" && (
            <SettingsPanel event={event} onToggleVisibility={handleTogglePublicVisibility} toggling={togglingVisibility}
              onSaveDetails={handleSaveEventDetails} savingDetails={savingDetails}
              onSaveWelcomeContent={handleSaveWelcomeContent} savingWelcomeContent={savingWelcome}
              isTournament onToggleSweeps={handleToggleSweepsVisibility} togglingSweeps={togglingSweeps}
              disciplines={disciplines} onUpdateDisciplineSchedule={handleUpdateDisciplineSchedule} />
          )}

          {activeTab === "disciplines" && (
          <div>
          <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">
            DISCIPLINES ({disciplines.length})
          </div>
          <div className="flex flex-col gap-2 mb-5">
            {disciplines.sort((a, b) => a.name.localeCompare(b.name)).map(disc => {
              const discBouts = bouts.filter(b => b.disciplineId === disc.id);
              const completed = discBouts.filter(b => b.status === "confirmed").length;
              return (
                <button key={disc.id} onClick={() => navigate(`/event/${eventId}/discipline/${disc.id}`)}
                  className="w-full text-left bg-dark-700 border border-dark-500 rounded-xl p-4 cursor-pointer hover:border-gray-600 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-display text-base font-bold text-white">{disc.name}</div>
                      <div className="text-gray-600 text-xs mt-1">
                        {disc.fighters?.length || 0} fighters • {disc.poolCount ?? disc.pools?.length ?? 0} pools • {discBouts.length} bouts
                      </div>
                    </div>
                    <div className="text-right">
                      {discBouts.length > 0 && (
            <div className="text-xs font-bold" style={{ color: completed === discBouts.length ? "#81C784" : "#bbb" }}>
                          {completed}/{discBouts.length}
                        </div>
                      )}
                      <div className="text-gray-700 text-lg">→</div>
                    </div>
                  </div>
                </button>
              );
            })}
            {disciplines.length === 0 && (
              <p className="text-gray-600 text-sm text-center py-4">No disciplines created.</p>
            )}
          </div>

          {!showAddDiscipline ? (
            <button onClick={() => setShowAddDiscipline(true)}
              className="w-full p-3 mb-5 bg-dark-700 border border-dashed border-dark-500 rounded-xl text-gray-500 text-sm font-bold cursor-pointer hover:text-gray-300 hover:border-gray-600">
              + Add Discipline
            </button>
          ) : (
            <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-5">
              <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">ADD DISCIPLINE</div>

              <div className="relative mb-2">
                <button type="button" onClick={() => setShowDisciplineDropdown(!showDisciplineDropdown)}
                  className="w-full p-3 bg-dark-800 border border-dashed border-dark-500 rounded-lg text-gray-500 text-sm cursor-pointer hover:text-gray-400 hover:border-gray-600 text-left flex items-center justify-between">
                  <span>Pick a preset...</span>
                  <span className="text-xs">{showDisciplineDropdown ? "▲" : "▼"}</span>
                </button>

                {showDisciplineDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-dark-800 border border-dark-500 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
                    {availableDisciplinePresets.map(preset => (
                      <button key={preset} type="button" onClick={() => handleAddDiscipline(preset)} disabled={addingDiscipline}
                        className="w-full text-left px-3 py-2.5 text-sm text-gray-300 hover:bg-dark-700 hover:text-white bg-transparent border-none cursor-pointer border-b border-dark-500/30 last:border-0 disabled:opacity-40">
                        {preset}
                      </button>
                    ))}
                    {availableDisciplinePresets.length === 0 && (
                      <div className="px-3 py-2.5 text-sm text-gray-600">All presets added</div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-2 mb-3">
          <input aria-label="Custom discipline name" value={customDisciplineName} onChange={(e) => setCustomDisciplineName(e.target.value)}
                  placeholder="Or type a custom discipline..."
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddDiscipline(customDisciplineName); } }}
                  className="flex-1 p-2.5 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400" />
                <button type="button" onClick={() => handleAddDiscipline(customDisciplineName)}
                  disabled={addingDiscipline || !customDisciplineName.trim()}
                  className="px-4 bg-dark-800 border border-dark-500 rounded-lg text-gray-400 text-sm font-bold cursor-pointer disabled:opacity-40 hover:text-white">
                  Add
                </button>
              </div>

              <button type="button" onClick={() => { setShowAddDiscipline(false); setShowDisciplineDropdown(false); setCustomDisciplineName(""); }}
                className="w-full p-2.5 bg-dark-800 border border-dark-500 rounded-lg text-gray-500 text-sm font-bold cursor-pointer">
                Cancel
              </button>
            </div>
          )}
          </div>
          )}
        </div>
      )}

      {/* SEMINAR/TRAINING MODE: Roster + Ad-hoc Bouts */}
      {!isTournament && (
        <>
          {/* Tab bar */}
          <div className="flex gap-1 mb-5 border-b border-dark-500 overflow-x-auto">
            {[
              { key: "settings", label: "SETTINGS" },
              { key: "disciplines", label: "EVENT" },
            ].map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)} aria-pressed={activeTab === t.key}
                className="px-4 py-2.5 font-display text-xs font-bold tracking-widest whitespace-nowrap border-b-2 bg-transparent cursor-pointer"
                style={{ color: activeTab === t.key ? "#fff" : "#bbb", borderColor: activeTab === t.key ? "#81C784" : "transparent" }}>
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === "settings" && (
            <SettingsPanel event={event} onToggleVisibility={handleTogglePublicVisibility} toggling={togglingVisibility}
              onSaveDetails={handleSaveEventDetails} savingDetails={savingDetails}
              onSaveWelcomeContent={handleSaveWelcomeContent} savingWelcomeContent={savingWelcome} />
          )}
          </>
      )}
      {!isTournament && activeTab !== "settings" && (
        <>
          {/* Roster */}
          <div className="mb-5">
            <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">PARTICIPANTS ({participants.length})</div>
            <form onSubmit={handleAddParticipants} className="mb-3">
          <textarea aria-label="Fighter names to add" value={newNames} onChange={(e) => setNewNames(e.target.value)}
                placeholder={"Add names (one per line):\nJohn Smith\nJane Doe"} rows={3}
                className="w-full p-3 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400 resize-none" />
              <button type="submit" disabled={adding || !newNames.trim()}
                className="mt-2 w-full p-2.5 bg-green-action-light text-white font-display font-bold rounded-lg text-sm tracking-wide disabled:opacity-40 border-none cursor-pointer">
                {adding ? "Adding..." : "+ Add Participants"}
              </button>
            </form>
            {!participantImport ? (
              <label className="block w-full p-2.5 mb-3 bg-dark-800 border border-dashed border-dark-500 rounded-lg text-gray-400 text-sm font-bold text-center cursor-pointer hover:text-white hover:border-gray-600">
                {participantParsing ? "Reading file..." : "Import names from Excel or CSV"}
                <input type="file" accept=".xlsx,.xls,.csv" onChange={handleParticipantFile} disabled={participantParsing} className="sr-only" />
              </label>
            ) : (
              <ParticipantImportPreview names={participantImport} existing={participants} importing={adding}
                onConfirm={handleConfirmParticipantImport} onCancel={() => setParticipantImport(null)} />
            )}
            {participants.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {participants.sort().map(name => (
                  <div key={name} className="flex items-center gap-1.5 bg-dark-700 border border-dark-500 rounded-lg px-3 py-1.5">
                    <span className="text-sm text-gray-300">{name}</span>
                    <button onClick={() => handleRemove(name)} aria-label={`Remove ${name}`} className="min-w-6 min-h-6 text-gray-600 hover:text-red-400 bg-transparent border-none cursor-pointer text-xs ml-1">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Create Bout */}
          {!showCreate ? (
            <button onClick={() => setShowCreate(true)} disabled={participants.length < 2}
              className="w-full p-3 mb-5 bg-green-action-light text-white font-display font-bold rounded-xl text-sm tracking-wide hover:opacity-90 border-none cursor-pointer disabled:opacity-40">
              + New Bout
            </button>
          ) : (
            <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-5">
              <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-4">NEW BOUT</div>

              {/* Blue */}
              <div className="mb-3">
                <label className="text-xs font-bold tracking-wider block mb-2" style={{ color: "#64B5F6" }}>BLUE CORNER</label>
                <div className="flex flex-wrap gap-1.5">
                  {availableBlue.sort().map(name => (
                    <button key={name} type="button" aria-pressed={fighterBlue === name} onClick={() => pickFighter(setFighterBlue, name)}
                      className="px-3 py-1.5 rounded-lg text-sm font-semibold border cursor-pointer transition-all"
                      style={{ background: fighterBlue === name ? "#1565C0" : "#111", borderColor: fighterBlue === name ? "#64B5F6" : "#666", color: fighterBlue === name ? "#fff" : "#64B5F6" }}>
                      {name}{fighterBlue === name ? " ✓" : ""}
                    </button>
                  ))}
                </div>
              </div>

              {/* Red */}
              <div className="mb-3">
                <label className="text-xs font-bold tracking-wider block mb-2" style={{ color: "#EF9A9A" }}>RED CORNER</label>
                <div className="flex flex-wrap gap-1.5">
                  {availableRed.sort().map(name => (
                    <button key={name} type="button" aria-pressed={fighterRed === name} onClick={() => pickFighter(setFighterRed, name)}
                      className="px-3 py-1.5 rounded-lg text-sm font-semibold border cursor-pointer transition-all"
                      style={{ background: fighterRed === name ? "#B71C1C" : "#111", borderColor: fighterRed === name ? "#EF9A9A" : "#666", color: fighterRed === name ? "#fff" : "#EF9A9A" }}>
                      {name}{fighterRed === name ? " ✓" : ""}
                    </button>
                  ))}
                </div>
              </div>

              {/* Referee */}
              <div className="mb-3">
                <label className="text-xs font-bold text-gray-500 tracking-wider block mb-2">REFEREE</label>
                <div className="flex flex-wrap gap-1.5">
                  {availableRefs.sort().map(name => {
                    const isSel = selectedRef === name;
                    return (
                      <button key={name} type="button" aria-pressed={isSel} onClick={() => setSelectedRef(isSel ? "" : name)}
                        className="px-3 py-1.5 rounded-lg text-sm font-semibold border cursor-pointer transition-all"
                        style={{ background: isSel ? "#E65100" : "#111", borderColor: isSel ? "#FFB74D" : "#666", color: isSel ? "#fff" : "#ddd" }}>
                        {name}{isSel ? " ✓" : ""}
                      </button>
                    );
                  })}
                </div>
                {!selectedRef && <p className="text-gray-600 text-xs mt-1.5">Pick a referee. The referee runs the bout and confirms each round.</p>}
              </div>

              {/* Judges */}
              <div className="mb-3">
                <label className="text-xs font-bold text-gray-500 tracking-wider block mb-2">JUDGES ({selectedJudges.length})</label>
                <div className="flex flex-wrap gap-1.5">
                  {availableJudges.sort().map(name => {
                    const isSel = selectedJudges.includes(name);
                    return (
                      <button key={name} type="button" aria-pressed={isSel} onClick={() => isSel ? setSelectedJudges(selectedJudges.filter(j => j !== name)) : setSelectedJudges([...selectedJudges, name])}
                        className="px-3 py-1.5 rounded-lg text-sm font-semibold border cursor-pointer transition-all"
                        style={{ background: isSel ? "#1B5E20" : "#111", borderColor: isSel ? "#81C784" : "#666", color: isSel ? "#fff" : "#ddd" }}>
                        {name} {isSel && "✓"}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Phase, Rounds, Time, Location */}
              <div className="flex gap-3 mb-3">
                <div className="flex-1">
                  <label className="text-xs font-bold text-gray-500 tracking-wider block mb-2">SCALE</label>
                  <div className="flex gap-1.5">
                    {PHASES.map(p => (
                      <button key={p.key} type="button" aria-pressed={phase === p.key} onClick={() => setPhase(p.key)}
                        className="flex-1 p-2 rounded-lg text-center text-xs font-display font-bold border cursor-pointer"
                        style={{ background: phase === p.key ? "#1a2a1a" : "#111", borderColor: phase === p.key ? "#81C784" : "#666", color: phase === p.key ? "#fff" : "#bbb" }}>
                        {p.label}{phase === p.key ? " ✓" : ""}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex-1">
                  <label className="text-xs font-bold text-gray-500 tracking-wider block mb-2">ROUNDS</label>
                  <div className="flex gap-1.5">
                    {ROUND_OPTIONS.map(n => (
                      <button key={n} type="button" aria-pressed={totalRounds === n} aria-label={`${n} rounds`} onClick={() => setTotalRounds(n)}
                        className="flex-1 p-2 rounded-lg text-center font-display text-base font-extrabold border cursor-pointer"
                        style={{ background: totalRounds === n ? "#1a2a1a" : "#111", borderColor: totalRounds === n ? "#81C784" : "#666", color: totalRounds === n ? "#fff" : "#bbb" }}>
                        {n}{totalRounds === n ? " ✓" : ""}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mb-4">
                <div className="flex-1">
                  <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1">TIME (optional)</label>
          <input aria-label="Bout time" value={boutTime} onChange={(e) => setBoutTime(e.target.value)} placeholder="e.g., 10:30 AM"
                    className="w-full p-2 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none" />
                </div>
                <div className="flex-1">
                  <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1">LOCATION (optional)</label>
          <input aria-label="Bout location" value={boutLocation} onChange={(e) => setBoutLocation(e.target.value)} placeholder="e.g., Ring 1"
                    className="w-full p-2 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none" />
                </div>
              </div>

              <div className="flex gap-2">
                <button type="button" onClick={() => { setShowCreate(false); setFighterBlue(""); setFighterRed(""); setSelectedRef(""); setSelectedJudges([]); }}
                  className="flex-1 p-3 bg-dark-800 border border-dark-500 rounded-lg text-gray-500 text-sm font-bold cursor-pointer">Cancel</button>
                <button type="button" onClick={handleCreateBout} disabled={creatingBout || !fighterBlue || !fighterRed || !selectedRef}
                  className="flex-1 p-3 bg-green-action-light text-white rounded-lg text-sm font-display font-bold tracking-wide disabled:opacity-40 border-none cursor-pointer">
                  {creatingBout ? "Creating..." : "Create Bout"}
                </button>
              </div>
            </div>
          )}

          {/* Bouts */}
          {activeBouts.length > 0 && (
            <div className="mb-5">
              <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-2">ACTIVE ({activeBouts.length})</div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-3 items-start">
                {activeBouts.map(b => <BoutCard key={b.id} bout={b} eventId={eventId} navigate={navigate} participants={participants} />)}
              </div>
            </div>
          )}
          {completedBouts.length > 0 && (
            <div className="mb-5">
          <div className="font-display text-xs font-bold text-green-action-text tracking-widest mb-2">COMPLETED ({completedBouts.length})</div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-3 items-start">
                {completedBouts.map(b => <BoutCard key={b.id} bout={b} eventId={eventId} navigate={navigate} participants={participants} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function JudgesPanel({ event, newOfficials, setNewOfficials, addingOfficials, onAdd, onRemove, onUpdateAffiliation, onUpdateRole, onUpdateEmail, onInvite, registrationStatus, invitingEmail }) {
  const officials = event.officials || [];
  const affiliations = event.officialAffiliations || {};
  const roles = event.officialRoles || {};
  const emails = event.officialEmails || {};

  return (
    <div>
      <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">
        JUDGES & REFS ({officials.length})
      </div>
      <p className="text-gray-600 text-xs mb-3">
        The full roster for this event, shared across every discipline. Referees can be assigned as
        either a bout's ref or a judge; judges can only be assigned as a judge.
      </p>

      <form onSubmit={onAdd} className="mb-4">
          <textarea aria-label="Officials to add" value={newOfficials} onChange={(e) => setNewOfficials(e.target.value)}
          placeholder={"Add judges & refs (one per line):\nMarco Rossi\nSarah Chen"} rows={2}
          className="w-full p-3 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400 resize-none" />
        <button type="submit" disabled={addingOfficials || !newOfficials.trim()}
          className="mt-2 w-full p-2.5 bg-green-action-light text-white font-display font-bold rounded-lg text-sm tracking-wide disabled:opacity-40 border-none cursor-pointer">
          {addingOfficials ? "Adding..." : "+ Add Judges & Refs"}
        </button>
      </form>

      {officials.length > 0 && (
        <div className="overflow-x-auto border border-dark-500 rounded-lg">
              <table className="w-full text-sm border-collapse">
                <caption className="sr-only">Event officials</caption>
            <thead>
              <tr className="bg-dark-700">
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">NAME</th>
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">ROLE</th>
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">AFFILIATION</th>
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">EMAIL</th>
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">REGISTRATION</th>
                  <th scope="col" className="p-2.5 w-8"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {[...officials].sort().map(name => {
                const email = emails[name] || "";
                const registered = email ? registrationStatus[email] : undefined;
                return (
                  <tr key={name} className="border-t border-dark-500">
                    <th scope="row" className="p-2.5 text-left font-normal text-gray-300 whitespace-nowrap">{name}</th>
                    <td className="p-2.5">
                      <select aria-label={`Role for ${name}`} value={roles[name] || "judge"} onChange={(e) => onUpdateRole(name, e.target.value)}
                        className="p-1.5 bg-dark-800 border border-dark-500 rounded-lg text-white text-xs outline-none focus:border-gray-400 cursor-pointer">
                        <option value="judge">Judge</option>
                        <option value="referee">Referee</option>
                      </select>
                    </td>
                    <td className="p-2.5">
                      <InlineTextCell label={`Affiliation for ${name}`} value={affiliations[name] || ""} onSave={(val) => onUpdateAffiliation(name, val)} />
                    </td>
                    <td className="p-2.5 min-w-[160px]">
                      <InlineTextCell label={`Email for ${name}`} type="email" value={email} placeholder="email@example.com"
                        onSave={(val) => onUpdateEmail(name, val)} />
                    </td>
                    <td className="p-2.5 whitespace-nowrap">
                      {!email ? (
                        <span className="text-xs text-gray-600">—</span>
                      ) : registered === undefined ? (
                        <span className="text-xs text-gray-600">Checking…</span>
                      ) : registered === null ? (
                        <span className="text-xs text-yellow-500">Couldn't check</span>
                      ) : registered ? (
                        <span className="text-xs font-bold" style={{ color: "#4CAF50" }}>✓ Registered</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Not registered</span>
                          <button onClick={() => onInvite(email)} disabled={invitingEmail === email}
                            className="px-2 py-1 bg-dark-800 border border-dark-500 rounded text-xs font-bold text-gray-300 cursor-pointer hover:border-gray-400 disabled:opacity-40">
                            {invitingEmail === email ? "Sending…" : "Invite"}
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="p-2.5 text-right">
                      <button onClick={() => onRemove(name)} aria-label={`Remove official ${name}`} className="min-w-6 min-h-6 text-gray-600 hover:text-red-400 bg-transparent border-none cursor-pointer text-xs">✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {officials.length === 0 && (
        <p className="text-gray-600 text-sm text-center py-4">No judges or refs added yet.</p>
      )}
    </div>
  );
}

function FightersPanel({ event, disciplines, newFighters, setNewFighters, addingFighters, onAdd, onRemove, onUpdateAffiliation, onUpdateEmail, onUpdateDisciplines, fighterAccountInfo, onDismissMismatch, onApproveClaim, claimActionUid, onDismissSharedEmail, rosterParsed, rosterMapping, rosterSummary, rosterParsing, rosterImporting, onRosterFile, onConfirmRosterMapping, onConfirmRosterImport, onCancelRosterImport }) {
  const fighters = event.fighters || [];
  const affiliations = event.fighterAffiliations || {};
  const emails = event.fighterEmails || {};
  const fighterDisciplines = event.fighterDisciplines || {};
  const mismatchDismissed = event.fighterMismatchDismissed || {};
  const pendingFighterClaims = event.pendingFighterClaims || {};
  const sharedEmailDismissed = event.fighterSharedEmailDismissed || {};

  // Same email entered for two different roster names (case-insensitive) — could be
  // one person managing multiple entries (a parent with a couple of kids) or an
  // admin typo/paste mistake. We can't tell which from the roster alone, so this is
  // just a heads-up here; if that person actually tries to sign in, api/claimFighter.js
  // turns it into an actionable pending claim below instead of guessing.
  const namesByEmail = {};
  for (const [n, e] of Object.entries(emails)) {
    const key = (e || "").trim().toLowerCase();
    if (!key) continue;
    if (!namesByEmail[key]) namesByEmail[key] = [];
    namesByEmail[key].push(n);
  }

  // uid -> claim, indexed by every fighter name it lists, so each row can look
  // itself up directly instead of scanning pendingFighterClaims per-render.
  const claimByName = {};
  for (const [uid, claim] of Object.entries(pendingFighterClaims)) {
    for (const n of claim.names) claimByName[n] = { uid, ...claim };
  }

  return (
    <div>
      <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">
        FIGHTERS ({fighters.length})
      </div>
      <p className="text-gray-600 text-xs mb-3">
        The single roster for the whole event. Use DISCIPLINES to pick which disciplines each
        fighter is entered in — they'll appear on that discipline's FIGHTERS tab automatically.
        Emails to fighters are composed and sent from the COMMUNICATIONS tab.
      </p>

      <div className="mb-4">
        <label className="w-full p-3 bg-dark-800 border border-dashed border-dark-500 rounded-lg text-gray-500 text-sm cursor-pointer hover:text-gray-400 hover:border-gray-600 flex items-center justify-center gap-2">
                  <input type="file" aria-label="Import roster file" accept=".csv,.xlsx,.xls" onChange={onRosterFile} disabled={rosterParsing} className="hidden" />
          <span>{rosterParsing ? "Reading..." : "⇪ Import roster (Excel/CSV)"}</span>
        </label>
        <p className="text-gray-600 text-[10px] mt-1">
          Works with any layout that has a name column — we'll guess what each other column
          means (email, affiliation, disciplines, judging) and let you confirm or correct it
          before importing.
        </p>
        {rosterParsed && rosterMapping && (
          <div className="mt-2">
            <RosterColumnMapping parsed={rosterParsed} defaultMapping={rosterMapping}
              onConfirm={onConfirmRosterMapping} onCancel={onCancelRosterImport} />
          </div>
        )}
        {rosterSummary && (
          <div className="mt-2">
            <RosterImportPreview summary={rosterSummary} onConfirm={onConfirmRosterImport} onCancel={onCancelRosterImport} importing={rosterImporting} />
          </div>
        )}
      </div>

      <form onSubmit={onAdd} className="mb-4">
          <textarea aria-label="Fighters to add" value={newFighters} onChange={(e) => setNewFighters(e.target.value)}
          placeholder={"Add fighters (one per line):\nMarco Rossi\nSarah Chen"} rows={2}
          className="w-full p-3 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400 resize-none" />
        <button type="submit" disabled={addingFighters || !newFighters.trim()}
          className="mt-2 w-full p-2.5 bg-green-action-light text-white font-display font-bold rounded-lg text-sm tracking-wide disabled:opacity-40 border-none cursor-pointer">
          {addingFighters ? "Adding..." : "+ Add Fighters"}
        </button>
      </form>

      {fighters.length > 0 && (
        <div className="overflow-x-auto border border-dark-500 rounded-lg">
              <table className="w-full text-sm border-collapse">
                <caption className="sr-only">Event fighters</caption>
            <thead>
              <tr className="bg-dark-700">
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">NAME</th>
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">EMAIL</th>
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">AFFILIATION</th>
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">DISCIPLINES</th>
                  <th scope="col" className="p-2.5 w-8"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {[...fighters].sort().map(name => {
                const email = (emails[name] || "").trim();
                const dupeNames = email && !sharedEmailDismissed[email.toLowerCase()]
                  ? namesByEmail[email.toLowerCase()].filter(n => n !== name) : [];
                const acct = email ? fighterAccountInfo[email] : null;
                const nameMismatch = dupeNames.length === 0 && acct?.registered && acct.displayName
                  && acct.displayName.trim().toLowerCase() !== name.trim().toLowerCase()
                  && mismatchDismissed[name] !== acct.displayName;
                const claim = claimByName[name];

                return (
                  <tr key={name} className="border-t border-dark-500">
                    <th scope="row" className="p-2.5 text-left font-normal text-gray-300 whitespace-nowrap align-top">{name}</th>
                    <td className="p-2.5 min-w-[160px] align-top">
                      <InlineTextCell label={`Email for ${name}`} type="email" value={emails[name] || ""} placeholder="email@example.com"
                        onSave={(val) => onUpdateEmail(name, val)} />
                      {claim ? (
                        <div className="text-[10px] text-blue-400 mt-0.5">
                          Sign-in wants to link: {claim.names.join(", ")} —{" "}
                          <button type="button" onClick={() => onApproveClaim(claim.uid, claim.names, claim.email)}
                            disabled={claimActionUid === claim.uid}
                            className="text-green-400 underline bg-transparent border-none cursor-pointer p-0 text-[10px] disabled:opacity-40">
                            {claimActionUid === claim.uid ? "Approving…" : "Approve"}
                          </button>{" "}
                          or fix the email above if this pairing is wrong
                        </div>
                      ) : dupeNames.length > 0 && (
                        <div className="text-[10px] text-yellow-500 mt-0.5">
                          Same email as {dupeNames.join(", ")} — fine if one person manages both{" "}
                          <button type="button" onClick={() => onDismissSharedEmail(email)}
                            className="text-yellow-500 underline bg-transparent border-none cursor-pointer p-0 text-[10px]">
                            Not a mistake
                          </button>{" "}
                          or fix the email above
                        </div>
                      )}
                      {nameMismatch && (
                        <div className="text-[10px] text-yellow-500 mt-0.5">
                          Registered as "{acct.displayName}" — double check this is {name}{" "}
                          <button type="button" onClick={() => onDismissMismatch(name, acct.displayName)}
                            className="text-yellow-500 underline bg-transparent border-none cursor-pointer p-0 text-[10px]">
                            This is them
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="p-2.5 align-top">
                      <InlineTextCell label={`Affiliation for ${name}`} value={affiliations[name] || ""} onSave={(val) => onUpdateAffiliation(name, val)} />
                    </td>
                    <td className="p-2.5 min-w-[140px] align-top">
                      <DisciplinesCell label={`Disciplines for ${name}`} disciplines={disciplines} selected={fighterDisciplines[name] || []}
                        onSave={(ids) => onUpdateDisciplines(name, ids)} />
                    </td>
                    <td className="p-2.5 text-right align-top">
                      <button onClick={() => onRemove(name)} aria-label={`Remove fighter ${name}`} className="min-w-6 min-h-6 text-gray-600 hover:text-red-400 bg-transparent border-none cursor-pointer text-xs">✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {fighters.length === 0 && (
        <p className="text-gray-600 text-sm text-center py-4">No fighters added yet.</p>
      )}
    </div>
  );
}

function CommunicationsPanel({ event, disciplines, user, onSaveWelcomeEmail, savingWelcomeEmail, onSaveCustomEmail, savingCustomEmail, showToast }) {
  return (
    <div>
      <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">
        COMMUNICATIONS
      </div>
      <p className="text-gray-600 text-xs mb-3">
        Compose and send email to fighters. Choose who receives it below — everyone with an
        email on file, or just those registered for a specific discipline. Names, affiliations,
        emails, and discipline registrations are managed on the FIGHTERS tab.
      </p>

      <EmailComposerPanel event={event} disciplines={disciplines} user={user}
        onSaveWelcomeEmail={onSaveWelcomeEmail} savingWelcomeEmail={savingWelcomeEmail}
        onSaveCustomEmail={onSaveCustomEmail} savingCustomEmail={savingCustomEmail} showToast={showToast} />
    </div>
  );
}

// { key: label, field prefix, endpoint, default subject, description, body placeholder }
// backing EmailComposerPanel's template dropdown — add an entry here for any future template.
const EMAIL_TEMPLATES = {
  welcome: {
    label: "Welcome Email",
    subjectField: "fighterWelcomeEmailSubject",
    bodyField: "fighterWelcomeEmail",
    headerImageField: "fighterWelcomeEmailHeaderImage",
    endpoint: "/api/sendFighterWelcomeEmail",
    defaultSubject: (event) => `Welcome to ${event.name}`,
    bodyPlaceholder: "Add any extra info fighters should know — schedule, what to bring, etc.",
    sentLabel: "Welcome email",
  },
  custom: {
    label: "Update Email",
    subjectField: "fighterCustomEmailSubject",
    bodyField: "fighterCustomEmailBody",
    headerImageField: "fighterCustomEmailHeaderImage",
    endpoint: "/api/sendFighterCustomEmail",
    defaultSubject: (event) => `Update from ${event.name}`,
    bodyPlaceholder: "Write your update here...",
    sentLabel: "Update email",
  },
};

function EmailComposerPanel({ event, disciplines, user, onSaveWelcomeEmail, savingWelcomeEmail, onSaveCustomEmail, savingCustomEmail, showToast }) {
  const fighters = event.fighters || [];
  const fighterDisciplines = event.fighterDisciplines || {};
  const [templateKey, setTemplateKey] = useState("welcome");
  const template = EMAIL_TEMPLATES[templateKey];

  // "all" or a discipline id — restricts preview + send to fighters registered for that
  // discipline (event.fighterDisciplines[name]), so e.g. a schedule change for one weapon
  // doesn't spam fighters who aren't entered in it.
  const [recipientGroup, setRecipientGroup] = useState("all");
  const recipientOptions = [
    { id: "all", label: "All Fighters", count: fighters.length },
    ...(disciplines || []).map(d => ({
      id: d.id,
      label: d.name,
      count: fighters.filter(name => (fighterDisciplines[name] || []).includes(d.id)).length,
    })),
  ];
  const recipients = recipientGroup === "all"
    ? fighters
    : fighters.filter(name => (fighterDisciplines[name] || []).includes(recipientGroup));
  const recipientLabel = recipientOptions.find(o => o.id === recipientGroup)?.label || "All Fighters";

  // Kept per-template so switching the dropdown mid-edit doesn't discard unsaved work in
  // the other template — only the active one is rendered/edited at a time.
  const [welcomeSubject, setWelcomeSubject] = useState(event.fighterWelcomeEmailSubject || "");
  const [welcomeBody, setWelcomeBody] = useState(toEditableHtml(event.fighterWelcomeEmail || ""));
  const [welcomeHeaderImage, setWelcomeHeaderImage] = useState(event.fighterWelcomeEmailHeaderImage || "");
  const [welcomeHeaderAlt, setWelcomeHeaderAlt] = useState(event.fighterWelcomeEmailHeaderImageAlt || "");
  const [customSubject, setCustomSubject] = useState(event.fighterCustomEmailSubject || "");
  const [customBody, setCustomBody] = useState(toEditableHtml(event.fighterCustomEmailBody || ""));
  const [customHeaderImage, setCustomHeaderImage] = useState(event.fighterCustomEmailHeaderImage || "");
  const [customHeaderAlt, setCustomHeaderAlt] = useState(event.fighterCustomEmailHeaderImageAlt || "");

  useEffect(() => {
    setWelcomeSubject(event.fighterWelcomeEmailSubject || "");
    setWelcomeBody(toEditableHtml(event.fighterWelcomeEmail || ""));
    setWelcomeHeaderImage(event.fighterWelcomeEmailHeaderImage || "");
    setWelcomeHeaderAlt(event.fighterWelcomeEmailHeaderImageAlt || "");
    setCustomSubject(event.fighterCustomEmailSubject || "");
    setCustomBody(toEditableHtml(event.fighterCustomEmailBody || ""));
    setCustomHeaderImage(event.fighterCustomEmailHeaderImage || "");
    setCustomHeaderAlt(event.fighterCustomEmailHeaderImageAlt || "");
  }, [event.id]);

  const isWelcome = templateKey === "welcome";
  const subject = isWelcome ? welcomeSubject : customSubject;
  const setSubject = isWelcome ? setWelcomeSubject : setCustomSubject;
  const body = isWelcome ? welcomeBody : customBody;
  const setBody = isWelcome ? setWelcomeBody : setCustomBody;
  const headerImage = isWelcome ? welcomeHeaderImage : customHeaderImage;
  const setHeaderImage = isWelcome ? setWelcomeHeaderImage : setCustomHeaderImage;
  const headerImageAlt = isWelcome ? welcomeHeaderAlt : customHeaderAlt;
  const setHeaderImageAlt = isWelcome ? setWelcomeHeaderAlt : setCustomHeaderAlt;
  const initialSubject = event[template.subjectField] || "";
  const initialBody = toEditableHtml(event[template.bodyField] || "");
  const initialHeaderImage = event[template.headerImageField] || "";
  const initialHeaderImageAlt = event[isWelcome ? "fighterWelcomeEmailHeaderImageAlt" : "fighterCustomEmailHeaderImageAlt"] || "";
  const onSave = isWelcome ? onSaveWelcomeEmail : onSaveCustomEmail;
  const saving = isWelcome ? savingWelcomeEmail : savingCustomEmail;

  const headerImageInputRef = useRef(null);
  const [uploadingHeaderImage, setUploadingHeaderImage] = useState(false);
  const [pendingHeaderImage, setPendingHeaderImage] = useState(null);
  const [pendingHeaderAlt, setPendingHeaderAlt] = useState("");
  const [pendingHeaderDecorative, setPendingHeaderDecorative] = useState(false);

  const handleHeaderImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPendingHeaderImage(file);
    setPendingHeaderAlt("");
    setPendingHeaderDecorative(false);
  };

  const uploadHeaderImage = async (e) => {
    e.preventDefault();
    if (!pendingHeaderImage || (!pendingHeaderDecorative && !pendingHeaderAlt.trim())) return;
    setUploadingHeaderImage(true);
    try {
      const url = await uploadRichContentImage(event.id, pendingHeaderImage);
      setHeaderImage(url);
      setHeaderImageAlt(pendingHeaderDecorative ? "" : pendingHeaderAlt.trim());
      setPendingHeaderImage(null);
    } catch (err) { console.error(err); alert(err.message || "Failed to upload image"); }
    setUploadingHeaderImage(false);
  };

  const [previewName, setPreviewName] = useState(fighters[0] || "");
  const [previewResult, setPreviewResult] = useState(null); // { subject, html }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [emailAllStatus, setEmailAllStatus] = useState(null); // { sending, sent, skipped, error, total }

  useEffect(() => {
    if (!recipients.includes(previewName)) setPreviewName([...recipients].sort()[0] || "");
  }, [recipients, previewName]);

  // Switching templates makes the previous template's preview stale (different endpoint/content).
  useEffect(() => {
    setPreviewResult(null);
  }, [templateKey]);

  // Switching recipient group makes the previous send-all status stale, but not the preview
  // itself — the preview only depends on previewName, which may be unchanged across a group
  // switch (e.g. the previewed fighter is in both groups), so clearing it here would leave
  // it stuck null since nothing would re-trigger the fetch effect below.
  useEffect(() => {
    setEmailAllStatus(null);
  }, [recipientGroup]);

  const dirty = subject !== initialSubject || body !== initialBody || headerImage !== initialHeaderImage || headerImageAlt !== initialHeaderImageAlt;

  // Live preview: re-renders the actual email (via the same endpoint that sends it) a
  // moment after subject/body/header image/previewed-fighter change, so the composer
  // always shows how the auto-generated greeting/footer and the admin's own content fit
  // together — no separate "Preview" click needed.
  useEffect(() => {
    if (!previewName) { setPreviewResult(null); return; }
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const idToken = await user.getIdToken();
        const res = await fetch(template.endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({ eventId: event.id, preview: true, name: previewName, subject, bodyHtml: body, headerImage, headerImageAlt }),
        });
        const data = await res.json();
        if (res.ok) setPreviewResult(data);
      } catch (err) { console.error(err); }
      setPreviewLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [template.endpoint, event.id, previewName, subject, body, headerImage, headerImageAlt, user]);

  const handleEmailAll = async () => {
    if (recipients.length === 0) return;
    setEmailAllStatus({ sending: true, sent: 0, skipped: 0, error: 0, total: recipients.length });
    const result = await sendToAllFighters(template.endpoint, event.id, [...recipients], setEmailAllStatus, user);
    setEmailAllStatus(result);
    if (showToast) showToast(`${template.sentLabel} sent to ${result.sent}/${result.total} fighters`);
  };

  return (
    <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-4">
      <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1">TEMPLATE</label>
      <select aria-label="Email template" value={templateKey} onChange={(e) => setTemplateKey(e.target.value)}
        className="w-full mb-3 p-2.5 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400">
        {Object.entries(EMAIL_TEMPLATES).map(([key, t]) => <option key={key} value={key}>{t.label}</option>)}
      </select>
      <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1">HEADER IMAGE</label>
      <div className="text-gray-600 text-xs mb-2">
        Shown at the very top of the email, above the greeting — e.g. your event logo or banner.
        Recommended: ~1200px wide, under 1MB (it scales to fit the email's 560px width automatically).
      </div>
      {headerImage ? (
        <div className="flex items-center gap-3 mb-3">
          <img src={headerImage} alt={headerImageAlt} className="h-14 rounded border border-dark-500 bg-dark-800 object-contain" />
          <button type="button" onClick={() => { setHeaderImage(""); setHeaderImageAlt(""); }}
            className="text-xs text-gray-400 hover:text-white bg-transparent border-none cursor-pointer underline">
            Remove
          </button>
        </div>
      ) : (
        <div className="mb-3">
          <input ref={headerImageInputRef} type="file" aria-label="Email header image file" accept="image/*" className="hidden" onChange={handleHeaderImageChange} />
          <button type="button" onClick={() => headerImageInputRef.current?.click()} disabled={uploadingHeaderImage}
            className="px-3 py-2 bg-dark-800 border border-dark-500 rounded-lg text-gray-300 text-xs font-bold cursor-pointer hover:border-gray-400 disabled:opacity-40">
            {uploadingHeaderImage ? "Uploading…" : "Add header image"}
          </button>
        </div>
      )}
      {pendingHeaderImage && <form onSubmit={uploadHeaderImage} className="p-3 mb-3 border border-dark-500 rounded-lg space-y-2">
        <div className="text-sm">Describe {pendingHeaderImage.name}</div>
        <label htmlFor="header-image-alt" className="block text-sm">Alternative text</label>
        <input id="header-image-alt" value={pendingHeaderAlt} onChange={(e) => setPendingHeaderAlt(e.target.value)} disabled={pendingHeaderDecorative}
          className="w-full p-2 bg-dark-800 border border-gray-400 rounded" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={pendingHeaderDecorative} onChange={(e) => setPendingHeaderDecorative(e.target.checked)} /> Decorative image</label>
        <button type="submit" disabled={uploadingHeaderImage || (!pendingHeaderDecorative && !pendingHeaderAlt.trim())} className="px-3 py-2 bg-green-action text-white rounded disabled:opacity-50">{uploadingHeaderImage ? "Uploading…" : "Use image"}</button>
        <button type="button" onClick={() => setPendingHeaderImage(null)} className="ml-2 px-3 py-2 border border-gray-400 rounded">Cancel</button>
      </form>}
      {headerImage && <div className="mb-3">
        <label htmlFor="saved-header-image-alt" className="block text-sm text-gray-300 mb-1">Header image alternative text (leave empty if decorative)</label>
        <input id="saved-header-image-alt" value={headerImageAlt} onChange={(e) => setHeaderImageAlt(e.target.value)} className="w-full p-2 bg-dark-800 border border-gray-400 rounded" />
      </div>}

      <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1">SUBJECT</label>
      <input aria-label="Email subject" value={subject} onChange={(e) => setSubject(e.target.value)}
        placeholder={template.defaultSubject(event)}
        className="w-full mb-3 p-2.5 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400" />

      <RichTextEditor key={`${event.id}-${templateKey}`} label="Email body" content={body} onChange={setBody}
        onImageUpload={(file) => uploadRichContentImage(event.id, file)}
        placeholder={template.bodyPlaceholder} />

      <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1 mt-3">SEND TO</label>
      <select aria-label="Email recipients" value={recipientGroup} onChange={(e) => setRecipientGroup(e.target.value)}
        className="w-full mb-3 p-2.5 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400">
        {recipientOptions.map(o => (
          <option key={o.id} value={o.id}>{o.label} ({o.count})</option>
        ))}
      </select>

      <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1 mt-3">LIVE PREVIEW</label>
      <div className="flex items-center gap-2 mb-2">
        <select aria-label="Preview email as fighter" value={previewName} onChange={(e) => setPreviewName(e.target.value)}
          disabled={recipients.length === 0}
          className="flex-1 p-2 bg-dark-800 border border-dark-500 rounded-lg text-white text-xs outline-none focus:border-gray-400 disabled:opacity-40">
          {recipients.length === 0
            ? <option value="">No fighters in this group</option>
            : [...recipients].sort().map(name => <option key={name} value={name}>Previewing as: {name}</option>)}
        </select>
        {previewLoading && <span className="text-gray-600 text-xs shrink-0">Updating…</span>}
      </div>
      {previewResult ? (
        <iframe title="Email preview" srcDoc={previewResult.html}
          className="w-full bg-white rounded-lg border border-dark-500"
          style={{ border: "none", minHeight: "360px" }} />
      ) : (
        <div className="w-full p-4 bg-dark-800 border border-dark-500 rounded-lg text-gray-600 text-xs text-center">
          {fighters.length === 0
            ? "Add a fighter on the FIGHTERS tab to see a live preview of this email."
            : recipients.length === 0
            ? "No fighters in this group yet — assign the discipline on the FIGHTERS tab."
            : "Loading preview…"}
        </div>
      )}

      <button onClick={() => onSave({ subject: subject.trim(), bodyHtml: body, headerImage, headerImageAlt })} disabled={saving || !dirty}
        className="w-full mt-3 p-2.5 bg-green-action-light text-white font-display font-bold rounded-lg text-sm tracking-wide disabled:opacity-40 border-none cursor-pointer">
        {saving ? "Saving..." : "Save Changes"}
      </button>

      {fighters.length > 0 && (
        <button onClick={handleEmailAll} disabled={!!emailAllStatus?.sending || dirty || recipients.length === 0}
          className="w-full mt-2 p-2.5 bg-dark-800 border border-dark-500 rounded-lg text-gray-300 text-sm font-display font-bold tracking-wide cursor-pointer hover:border-gray-400 disabled:opacity-40">
          {emailAllStatus?.sending ? "Sending…" : `Email ${recipientLabel} (${recipients.length})`}
        </button>
      )}
      {dirty && fighters.length > 0 && (
        <p className="text-gray-600 text-xs mt-2 text-center">Save changes before sending.</p>
      )}
      {emailAllStatus && !dirty && (
        <p className="text-gray-600 text-xs mt-2 text-center">
          Sent {emailAllStatus.sent}/{emailAllStatus.total}
          {emailAllStatus.skipped > 0 && ` (${emailAllStatus.skipped} skipped, no email)`}
          {emailAllStatus.error > 0 && ` (${emailAllStatus.error} failed)`}
        </p>
      )}
    </div>
  );
}

// Chunks the event's fighter roster into batches of MAX_NAMES_PER_REQUEST (mirrors the api
// routes' own limit) and posts each batch to `endpoint`, tallying sent/skipped/error as it goes.
async function sendToAllFighters(endpoint, eventId, names, onProgress, user) {
  let sent = 0, skipped = 0, error = 0;
  for (let i = 0; i < names.length; i += 50) {
    const chunk = names.slice(i, i + 50);
    try {
      const idToken = await user.getIdToken();
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ eventId, names: chunk }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      const results = data.results || {};
      for (const name of chunk) {
        const result = results[name];
        if (result === "sent") sent++;
        else if (result === "no-email") skipped++;
        else error++;
      }
    } catch (err) {
      console.error(err);
      error += chunk.length;
    }
    onProgress({ sending: true, sent, skipped, error, total: names.length });
  }
  return { sending: false, sent, skipped, error, total: names.length };
}

async function fetchRegistrationInfo(user, eventId, emails) {
  const idToken = await user.getIdToken();
  const combined = { status: {}, names: {} };
  for (let i = 0; i < emails.length; i += 200) {
    const res = await fetch("/api/checkRegistration", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ eventId, emails: emails.slice(i, i + 200) }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `checkRegistration returned ${res.status}`);
    Object.assign(combined.status, data.status || {});
    Object.assign(combined.names, data.names || {});
  }
  return combined;
}

// Popover checklist for toggling which disciplines a fighter is registered for
// (event.fighterDisciplines[name], keyed by discipline id) — toggling a box saves
// immediately, mirroring the instant-toggle visibility switches in DisciplineDetail
// rather than requiring a separate confirm step.
function DisciplinesCell({ label, disciplines, selected, onSave }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const selectedNames = disciplines.filter(d => selected.includes(d.id)).map(d => d.name);

  const toggle = (discId) => {
    const next = selected.includes(discId) ? selected.filter(id => id !== discId) : [...selected, discId];
    onSave(next);
  };

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(!open)} onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }} aria-label={label} aria-expanded={open} aria-controls={panelId}
        className="w-full text-left bg-transparent border-none outline-none text-sm p-0 cursor-pointer"
        style={{ color: selectedNames.length ? "#ccc" : "#bbb" }}>
        {selectedNames.length ? selectedNames.join(", ") : "— assign —"}
      </button>

      {open && (
        <div id={panelId} onKeyDown={(e) => { if (e.key === "Escape") { setOpen(false); e.currentTarget.previousElementSibling?.focus(); } }} className="absolute top-full left-0 mt-1 bg-dark-800 border border-dark-500 rounded-lg shadow-lg z-20 min-w-[180px] p-1">
          {disciplines.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-gray-600">No disciplines yet</div>
          )}
          {disciplines.map(d => (
            <label key={d.id} className="flex items-center gap-2 px-2 py-1.5 text-sm text-gray-300 hover:bg-dark-700 rounded cursor-pointer">
              <input type="checkbox" checked={selected.includes(d.id)} onChange={() => toggle(d.id)} />
              {d.name}
            </label>
          ))}
          <button type="button" onClick={() => setOpen(false)}
            className="w-full mt-1 p-1.5 bg-dark-700 border border-dark-500 rounded text-gray-400 text-xs font-bold cursor-pointer hover:text-white">
            Done
          </button>
        </div>
      )}
    </div>
  );
}

// Pre-fills the event's public Welcome Message the first time an admin opens SETTINGS
// (i.e. event.welcomeContent is still unset) so they're editing/deleting a starting point
// rather than writing from a blank box. Bracketed [placeholders] mark what's event-specific;
// headings/lists use only tags sanitizeHtml.js allows, so it round-trips through
// RichTextEditor and the public event page's dangerouslySetInnerHTML as-is. Takes the event
// object (not just its id) so the intro line can use the real event name and the Schedule
// section can link straight to that event's own public SCHEDULE tab (Location/Parking/etc.
// stay bracketed placeholders since only the admin knows those).
const getDefaultEventWelcomeContent = (event) => `
<p>Welcome to the ${escapeHtml(event.name || "tournament")}!</p>
<h2>Schedule</h2>
<p>For check-in/gear-check and pool start times, see the <a href="/public/${event.id}?tab=schedule">Schedule</a> tab above.</p>
<h2>Location</h2>
<p>[Venue name, address, and any building/room notes.]</p>
<h2>Parking &amp; Arrival</h2>
<p>[Parking instructions, nearest lot, rideshare drop-off notes.]</p>
<h2>What to Bring</h2>
<ul>
<li>Full protective gear per the [ruleset]</li>
<li>Weapons for your registered discipline(s)</li>
<li>Water, snacks, a change of clothes</li>
</ul>
<h2>Rules &amp; Safety Reminders</h2>
<p>[Any tournament-specific rules, weapon control checks, code of conduct link.]</p>
<h2>Questions</h2>
<p>Reach out anytime at [contact email/phone] — we're happy to help.</p>
`.trim();

function SettingsPanel({ event, onToggleVisibility, toggling, onSaveDetails, savingDetails, onSaveWelcomeContent, savingWelcomeContent, isTournament, onToggleSweeps, togglingSweeps, disciplines = [], onUpdateDisciplineSchedule }) {
  const isPublic = !!event.isPublic;
  const sweepsVisible = !!event.sweepsVisible;
  const [name, setName] = useState(event.name || "");
  const [startDate, setStartDate] = useState(event.startDate || "");
  const [endDate, setEndDate] = useState(event.endDate || "");
  const [location, setLocation] = useState(event.location || "");

  const initialWelcomeContent = toEditableHtml(event.welcomeContent || "");
  const [welcomeContent, setWelcomeContent] = useState(toEditableHtml(event.welcomeContent || getDefaultEventWelcomeContent(event)));

  useEffect(() => {
    setName(event.name || "");
    setStartDate(event.startDate || "");
    setEndDate(event.endDate || "");
    setLocation(event.location || "");
    setWelcomeContent(toEditableHtml(event.welcomeContent || getDefaultEventWelcomeContent(event)));
  }, [event.id]);

  // Auto-saves the Welcome Message default the first time this event's SETTINGS tab sees
  // nothing saved yet, so the default is live on the public event page without the admin
  // needing to click Save — any further edits still require an explicit Save. Guarded by a
  // ref (not just the welcomeContent check) so the write fires once per event rather than
  // repeating on every render while the save is in flight.
  const autoSavedWelcomeContentRef = useRef(null);
  useEffect(() => {
    if (event.welcomeContent || autoSavedWelcomeContentRef.current === event.id) return;
    autoSavedWelcomeContentRef.current = event.id;
    onSaveWelcomeContent(getDefaultEventWelcomeContent(event));
  }, [event.id, event.welcomeContent, onSaveWelcomeContent]);

  const dirty = name.trim() !== (event.name || "") || startDate !== (event.startDate || "")
    || endDate !== (event.endDate || "") || location.trim() !== (event.location || "");
  const welcomeDirty = welcomeContent !== initialWelcomeContent;

  const handleSave = () => {
    if (!name.trim()) return;
    // A single-day event just needs a start date — keep end date in sync so
    // formatDateRange renders it as one day instead of an open-ended range.
    onSaveDetails({ name: name.trim(), startDate, endDate: endDate || startDate, location: location.trim() });
  };

  // All open by default so nothing looks hidden on first load — collapsing is purely an
  // admin convenience for shrinking a section (e.g. the Welcome Message's now-longer
  // default template) once they're done with it.
  const [openSections, setOpenSections] = useState({ details: true, schedule: true, welcome: true, visibility: true, sweeps: true });
  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div>
      <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">SETTINGS</div>

      {/* Event Details */}
      <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-4">
        <button onClick={() => toggleSection("details")} aria-expanded={openSections.details}
          className="w-full flex items-center justify-between bg-transparent border-none cursor-pointer p-0 text-left">
          <div className="font-display text-sm font-bold text-white">Event Details</div>
          <span aria-hidden="true" className="text-gray-600 text-xs">{openSections.details ? "▲" : "▼"}</span>
        </button>

        {openSections.details && (
        <>
        <div className="mb-3 mt-3">
          <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1">NAME</label>
        <input aria-label="Event name" value={name} onChange={(e) => setName(e.target.value)}
            className="w-full p-2.5 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400" />
        </div>

        <div className="flex gap-3 mb-3">
          <div className="flex-1">
            <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1">START DATE</label>
        <input aria-label="Event start date" type="date" value={startDate}
              onChange={(e) => { setStartDate(e.target.value); if (endDate && e.target.value > endDate) setEndDate(e.target.value); }}
              className="w-full p-2.5 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400" />
          </div>
          <div className="flex-1">
            <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1">END DATE</label>
        <input aria-label="Event end date" type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)}
              className="w-full p-2.5 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400" />
          </div>
        </div>

        <div className="mb-4">
          <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1">LOCATION</label>
        <input aria-label="Event location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g., Springfield, IL"
            className="w-full p-2.5 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400" />
        </div>

        <button onClick={handleSave} disabled={savingDetails || !dirty || !name.trim()}
          className="w-full p-2.5 bg-green-action-light text-white font-display font-bold rounded-lg text-sm tracking-wide disabled:opacity-40 border-none cursor-pointer">
          {savingDetails ? "Saving..." : "Save Changes"}
        </button>
        </>
        )}
      </div>

      {isTournament && (
      <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-4">
        <button onClick={() => toggleSection("schedule")} aria-expanded={openSections.schedule}
          className="w-full flex items-center justify-between bg-transparent border-none cursor-pointer p-0 text-left">
          <div className="font-display text-sm font-bold text-white">Schedule</div>
          <span aria-hidden="true" className="text-gray-600 text-xs">{openSections.schedule ? "▲" : "▼"}</span>
        </button>

        {openSections.schedule && (
        <>
        <div className="text-gray-600 text-xs mb-3 mt-1">
          Check-in/gear-check and pool start day &amp; time for each discipline. Shown on the SCHEDULE tab of the public event page.
        </div>

        {disciplines.length === 0 ? (
          <p className="text-gray-600 text-sm text-center py-4">Add a discipline on the DISCIPLINES tab to set its schedule.</p>
        ) : (
          <div className="overflow-x-auto border border-dark-500 rounded-lg">
            <table className="w-full text-sm border-collapse">
              <caption className="sr-only">Discipline schedule</caption>
              <thead>
                <tr className="bg-dark-800">
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">DISCIPLINE</th>
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">EVENT DATE</th>
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">CHECK-IN TIME</th>
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">POOL START TIME</th>
                </tr>
              </thead>
              <tbody>
                {[...disciplines].sort((a, b) => a.name.localeCompare(b.name)).map(disc => {
                  const sched = event.disciplineSchedules?.[disc.id] || {};
                  return (
                    <tr key={disc.id} className="border-t border-dark-500">
                      <th scope="row" className="p-2.5 text-left font-normal text-gray-300 whitespace-nowrap">{disc.name}</th>
                      <td className="p-2.5">
                        <InlineTextCell label={`Event date for ${disc.name}`} type="date" value={sched.eventDate || ""}
                          onSave={(val) => onUpdateDisciplineSchedule(disc.id, "eventDate", val)} />
                      </td>
                      <td className="p-2.5">
                        <TimeEntryCell label={`Check-in time for ${disc.name}`} value={sched.checkInTime || ""}
                          onSave={(val) => onUpdateDisciplineSchedule(disc.id, "checkInTime", val)} />
                      </td>
                      <td className="p-2.5">
                        <TimeEntryCell label={`Pool start time for ${disc.name}`} value={sched.poolStartTime || ""}
                          onSave={(val) => onUpdateDisciplineSchedule(disc.id, "poolStartTime", val)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </div>
      )}

      {/* Welcome Message */}
      <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-4">
        <button onClick={() => toggleSection("welcome")} aria-expanded={openSections.welcome}
          className="w-full flex items-center justify-between bg-transparent border-none cursor-pointer p-0 text-left">
          <div className="font-display text-sm font-bold text-white">Welcome Message</div>
          <span aria-hidden="true" className="text-gray-600 text-xs">{openSections.welcome ? "▲" : "▼"}</span>
        </button>

        {openSections.welcome && (
        <>
        <div className="text-gray-600 text-xs mb-3 mt-1">
          Shown on the HOME tab of the public event page. Use it for a welcome message, parking info, discipline schedules, or anything else visitors should know.
        </div>
      <RichTextEditor key={event.id} label="Event welcome message" content={welcomeContent} onChange={setWelcomeContent}
          onImageUpload={(file) => uploadRichContentImage(event.id, file)}
          placeholder="Welcome to the tournament! Add parking info, schedule, etc." />
        <button onClick={() => onSaveWelcomeContent(welcomeContent)} disabled={savingWelcomeContent || !welcomeDirty}
          className="w-full mt-3 p-2.5 bg-green-action-light text-white font-display font-bold rounded-lg text-sm tracking-wide disabled:opacity-40 border-none cursor-pointer">
          {savingWelcomeContent ? "Saving..." : "Save Changes"}
        </button>

        {!isHtmlEmpty(welcomeContent) && (
          <div className="mt-4">
            <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-2">PREVIEW</div>
            <div className="rich-content bg-dark-800 border border-dark-500 rounded-xl p-4 text-sm text-gray-300"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(welcomeContent) }} />
          </div>
        )}
        </>
        )}
      </div>

      <div className="bg-dark-700 border border-dark-500 rounded-xl p-4">
        <div className="flex items-center justify-between gap-4">
          <button onClick={() => toggleSection("visibility")} aria-expanded={openSections.visibility}
            className="flex items-center gap-2 bg-transparent border-none cursor-pointer p-0 text-left">
            <div className="font-display text-sm font-bold text-white">Public Visibility</div>
          <span aria-hidden="true" className="text-gray-600 text-xs">{openSections.visibility ? "▲" : "▼"}</span>
          </button>
          <button onClick={onToggleVisibility} disabled={toggling} role="switch" aria-checked={isPublic} aria-label="Event public visibility"
            className="shrink-0 w-14 h-8 rounded-full relative border-none cursor-pointer disabled:opacity-40 transition-colors"
            style={{ background: isPublic ? "#1B5E20" : "#555", border: "1px solid #aaa" }}>
            <span className="absolute top-1 w-6 h-6 rounded-full bg-white transition-all"
              style={{ left: isPublic ? "1.75rem" : "0.25rem" }} />
          </button>
        </div>
        {openSections.visibility && (
          <div className="text-xs font-bold mt-3" style={{ color: isPublic ? "#81C784" : "#bbb" }}>
            {isPublic ? "Public — visible on the home page" : "Private — only reachable by code"}
          </div>
        )}
      </div>

      {isTournament && (
        <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mt-4">
          <div className="flex items-center justify-between gap-4">
            <button onClick={() => toggleSection("sweeps")} aria-expanded={openSections.sweeps}
              className="flex items-center gap-2 bg-transparent border-none cursor-pointer p-0 text-left">
              <div className="font-display text-sm font-bold text-white">Sweeps Results Visibility</div>
              <span aria-hidden="true" className="text-gray-600 text-xs">{openSections.sweeps ? "▲" : "▼"}</span>
            </button>
            <button onClick={onToggleSweeps} disabled={togglingSweeps} role="switch" aria-checked={sweepsVisible} aria-label="Sweeps results public visibility"
              className="shrink-0 w-14 h-8 rounded-full relative border-none cursor-pointer disabled:opacity-40 transition-colors"
              style={{ background: sweepsVisible ? "#1B5E20" : "#555", border: "1px solid #aaa" }}>
              <span className="absolute top-1 w-6 h-6 rounded-full bg-white transition-all"
                style={{ left: sweepsVisible ? "1.75rem" : "0.25rem" }} />
            </button>
          </div>
          {openSections.sweeps && (
            <div className="text-xs font-bold mt-3" style={{ color: sweepsVisible ? "#81C784" : "#bbb" }}>
              {sweepsVisible ? "Released — visible to the public" : "Hidden — not yet released"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ParticipantImportPreview({ names, existing, importing, onConfirm, onCancel }) {
  const newNames = names.filter(n => !existing.includes(n));
  const alreadyAdded = names.length - newNames.length;
  return (
    <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-3">
      <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-2">IMPORT PREVIEW</div>
      <p className="text-gray-400 text-sm mb-3">
        Found {names.length} name{names.length === 1 ? "" : "s"}
        {alreadyAdded > 0 && <> &middot; {alreadyAdded} already on the list</>}
      </p>
      {newNames.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3 max-h-48 overflow-y-auto">
          {newNames.map(n => <span key={n} className="bg-dark-800 border border-dark-500 rounded-lg px-2.5 py-1 text-sm text-gray-300">{n}</span>)}
        </div>
      )}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel}
          className="flex-1 p-2.5 bg-dark-800 border border-dark-500 rounded-lg text-gray-500 text-sm font-bold cursor-pointer">Cancel</button>
        <button type="button" onClick={() => onConfirm(newNames)} disabled={importing || newNames.length === 0}
          className="flex-1 p-2.5 bg-green-action-light text-white rounded-lg text-sm font-display font-bold tracking-wide disabled:opacity-40 border-none cursor-pointer">
          {importing ? "Adding..." : `Add ${newNames.length} Participant${newNames.length === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  );
}

function BoutCard({ bout, eventId, navigate, participants = [] }) {
  const statusStyle = {
    waiting: { color: "#bbb", label: "Waiting" }, scoring: { color: "#64B5F6", label: "Scoring" },
    submitted: { color: "#FFB300", label: "Review" }, confirmed: { color: "#4CAF50", label: "✓ Done" },
  }[bout.status] || { color: "#bbb", label: bout.status };
  const judgesDone = Object.values(bout.judgeSubmissions || {}).filter(s => s.complete).length;
  const officialCode = bout.officialCode || bout.code;
  const isDone = bout.status === "confirmed";
  // Seminar/training bouts have no discipline, so their scorecards live under the event.
  const detailPath = bout.disciplineId
    ? `/event/${eventId}/discipline/${bout.disciplineId}/bout/${bout.id}`
    : `/event/${eventId}/bout/${bout.id}`;

  return (
    <div className="bg-dark-700 border border-dark-500 rounded-xl p-3 mb-2">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="font-display text-sm font-bold" style={{ color: "#64B5F6" }}>{bout.fighterBlue}</span>
          <span className="text-gray-600 text-xs">vs</span>
          <span className="font-display text-sm font-bold" style={{ color: "#EF9A9A" }}>{bout.fighterRed}</span>
        </div>
        <span className="font-display text-xs font-bold" style={{ color: statusStyle.color }}>{statusStyle.label}</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
        {bout.refName ? <span>Ref: <span className="text-gray-300">{bout.refName}</span></span> : isDone ? <span>No ref</span> : (
          // Bouts created before the referee picker existed have no ref, so nobody can
          // run them; let the admin assign one here.
          <select aria-label={`Referee for ${bout.fighterBlue} versus ${bout.fighterRed}`} value=""
            onChange={(e) => e.target.value && updateBout(eventId, bout.id, { refName: e.target.value }).catch((err) => { console.error(err); alert("Failed to assign referee"); })}
            className="p-1 bg-dark-800 border border-yellow-700 rounded text-yellow-300 text-xs">
            <option value="">Assign a referee…</option>
            {participants.filter(p => p !== bout.fighterBlue && p !== bout.fighterRed && !(bout.judges || []).includes(p)).sort()
              .map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
        {bout.judges?.length > 0 && <span>{judgesDone}/{bout.judges.length} scored</span>}
        {bout.time && <span>{bout.time}</span>}
        {bout.location && <span>{bout.location}</span>}
        {bout.confirmedResult && (
          <span className="ml-auto">
            <span className="font-display font-bold" style={{ color: "#1E88E5" }}>{bout.confirmedResult.scoreB}</span>
            <span className="mx-1 text-gray-700">–</span>
            <span className="font-display font-bold" style={{ color: "#E53935" }}>{bout.confirmedResult.scoreA}</span>
          </span>
        )}
      </div>
      {!isDone && (officialCode || bout.spectateCode) && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-gray-500">
          {officialCode && <span>Official code: <span className="font-display font-bold text-gray-200 tracking-wider">{officialCode}</span></span>}
          {bout.spectateCode && <span>Spectator code: <span className="font-display font-bold text-gray-300 tracking-wider">{bout.spectateCode}</span></span>}
        </div>
      )}
      {!isDone && officialCode && (
        <CodeQR path={`/bout/join?code=${encodeURIComponent(officialCode)}`} label="Show official QR" defaultOpen={false}
          caption="Referee and judges scan to join" />
      )}
      <button type="button" onClick={() => navigate(detailPath)}
        className="mt-2 w-full p-2 bg-dark-800 border border-dark-500 rounded-lg text-gray-400 text-xs font-bold cursor-pointer hover:text-white">
        View scorecards →
      </button>
    </div>
  );
}
